/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton, Link, Typography2 } from '@coursera/cds-core';
import { ExternalLinkIcon, FileIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/assess-common';

type Props = {
  fileUrl: string;
  disabled: boolean;
  filename?: string;
  onRemoveUploadedFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

const styles = {
  container: () => css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-50);
    position: relative;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    overflow: hidden;
  `,

  icon: () => css`
    color: var(--cds-color-neutral-primary);
    flex-shrink: 0;
  `,

  link: () => css`
    padding: var(--cds-spacing-150);
    text-decoration: none;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    min-width: 0;

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,

  filename: () => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: var(--cds-spacing-100);
  `,

  iconButton: () => css`
    flex-shrink: 0;
    width: 40px;
  `,
};

const UploadedFileBlock = ({ fileUrl, filename, disabled, onRemoveUploadedFile }: Props) => {
  return (
    <div css={styles.container} data-testid="uploaded-file-block">
      <Link
        rel="noopener noreferrer"
        target="_blank"
        href={fileUrl}
        aria-label={_t('Open uploaded file #{filename}', { filename })}
        css={styles.link}
        data-testid="uploaded-file-block-link"
      >
        {disabled ? <ExternalLinkIcon size="small" css={styles.icon} /> : <FileIcon size="small" css={styles.icon} />}
        {filename && (
          <Typography2
            variant="actionSecondary"
            component="span"
            css={styles.filename}
            data-testid="uploaded-file-block-filename"
          >
            {filename}
          </Typography2>
        )}
      </Link>
      {!disabled && (
        <IconButton
          variant="ghost"
          intent="delete"
          size="small"
          onClick={onRemoveUploadedFile}
          onKeyUp={onRemoveUploadedFile}
          tabIndex={0}
          aria-label={_t('Remove uploaded file')}
          css={styles.iconButton}
        />
      )}
    </div>
  );
};

export default UploadedFileBlock;
